import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Radix's Select/Popover/Dropdown positioning uses ResizeObserver internally, and rapid
// open/close cycles can trigger the browser's own benign "ResizeObserver loop completed with
// undelivered notifications" warning. Wrapping every ResizeObserver callback in
// requestAnimationFrame is the standard root-cause fix — it breaks the synchronous
// resize-during-resize-callback chain that produces the warning in the first place. (The dev
// overlay itself is additionally filtered for this specific message in craco.config.js, since
// CRA's overlay otherwise treats it as fatal and blanks the whole app.)
const OriginalResizeObserver = window.ResizeObserver;
if (OriginalResizeObserver) {
  window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      // A permanent 4xx (permission-denied, not-found, etc.) will never succeed on retry — retry
      // only on network/5xx failures, so a genuine 403 surfaces its ErrorState within ~1s instead
      // of ~15-20s of exponential-backoff retries (found by testing_agent_v4 iteration 17).
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
    },
  },
});

// BUG FIX (money correctness/freshness) - see api.js's response interceptor: every successful
// write anywhere in the app invalidates the ENTIRE cache, so every currently-open screen's
// dues/balance numbers refetch within milliseconds instead of waiting out the 60s `staleTime`
// above. `invalidateQueries()` with no key filter matches every query and immediately refetches
// every ACTIVE one - a global, generic fix rather than per-mutation `onSuccess` wiring that is
// easy to miss on any one of the app's many money-displaying screens.
window.addEventListener("fitfrik:data-changed", () => {
  queryClient.invalidateQueries();
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
